import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { handleApiError } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import toast from 'react-hot-toast';
import { User, Shield, Calendar, Clock, Key, Mail, Camera } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!passwordForm.current_password) newErrors.current_password = 'Password saat ini wajib diisi';
    if (!passwordForm.new_password) newErrors.new_password = 'Password baru wajib diisi';
    else if (passwordForm.new_password.length < 6) newErrors.new_password = 'Password minimal 6 karakter';
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      newErrors.confirm_password = 'Konfirmasi password tidak cocok';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const res = await api.put('/auth/change-password', passwordForm);
      if (res.data.success) {
        toast.success('Password berhasil diubah');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const roleBadgeColor = (roleName) => (roleName === 'owner' ? 'badge-primary' : 'badge-gray');

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="page-title">Profil Saya</h1>
        <p className="page-subtitle">Kelola informasi dan keamanan akun Anda</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Avatar & Info Card */}
        <div className="card lg:col-span-1">
          <div className="card-body text-center">
            <div className="relative inline-block">
              <div className="mx-auto mb-4 flex h-28 w-28 items-center justify-center rounded-full bg-primary-600 ring-4 ring-primary-50 dark:ring-primary-950">
                <span className="text-4xl font-semibold text-primary-900">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <button className="absolute bottom-2 right-2 p-2 rounded-full bg-white dark:bg-gray-700 shadow-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                <Camera className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{user?.name || 'User'}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">@{user?.username}</p>
            <span className={`badge ${roleBadgeColor(user?.role?.name)} text-sm px-3 py-1`}>
              {user?.role?.displayName || user?.role?.label || user?.role?.name || '-'}
            </span>
          </div>
        </div>

        {/* Account Info & Change Password */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Info */}
          <div className="card">
            <div className="card-header flex items-center">
              <User className="h-5 w-5 mr-2 text-primary-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Informasi Akun</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <User className="h-4 w-4 mr-2" /> Nama Lengkap
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <Mail className="h-4 w-4 mr-2" /> Email
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.email || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <Shield className="h-4 w-4 mr-2" /> Role
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.role?.displayName || user?.role?.label || user?.role?.name || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <Calendar className="h-4 w-4 mr-2" /> Bergabung Sejak
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.created_at ? formatDateTime(user.created_at) : '-'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <Clock className="h-4 w-4 mr-2" /> Terakhir Login
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.last_login ? formatDateTime(user.last_login) : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="card">
            <div className="card-header flex items-center">
              <Key className="h-5 w-5 mr-2 text-primary-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Ubah Password</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div>
                  <label className="label">Password Saat Ini</label>
                  <input
                    type="password"
                    className={`input ${errors.current_password ? 'input-error' : ''}`}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    placeholder="Masukkan password saat ini"
                  />
                  {errors.current_password && (
                    <p className="mt-1 text-sm text-danger-600">{errors.current_password}</p>
                  )}
                </div>
                <div>
                  <label className="label">Password Baru</label>
                  <input
                    type="password"
                    className={`input ${errors.new_password ? 'input-error' : ''}`}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    placeholder="Minimal 6 karakter"
                  />
                  {errors.new_password && (
                    <p className="mt-1 text-sm text-danger-600">{errors.new_password}</p>
                  )}
                </div>
                <div>
                  <label className="label">Konfirmasi Password Baru</label>
                  <input
                    type="password"
                    className={`input ${errors.confirm_password ? 'input-error' : ''}`}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    placeholder="Ulangi password baru"
                  />
                  {errors.confirm_password && (
                    <p className="mt-1 text-sm text-danger-600">{errors.confirm_password}</p>
                  )}
                </div>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Menyimpan...' : 'Ubah Password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}